import type { CustomerProfile } from '@/types/models';

type CustomerProfileCacheEntry = {
  data: CustomerProfile | null;
  loadedAt?: number;
  inFlightPromise?: Promise<CustomerProfile | null>;
};

const customerProfileCache = new Map<string, CustomerProfileCacheEntry>();

export const getCachedCustomerProfile = (
  customerId: string,
): CustomerProfile | null | undefined => customerProfileCache.get(customerId)?.data;

export const setCachedCustomerProfile = (
  customerId: string,
  profile: CustomerProfile | null,
): void => {
  customerProfileCache.set(customerId, {
    data: profile,
    loadedAt: Date.now(),
  });
};

export const clearCachedCustomerProfile = (customerId: string): void => {
  customerProfileCache.delete(customerId);
};

export const getOrLoadCustomerProfile = async (
  customerId: string,
  loader: () => Promise<CustomerProfile | null>,
): Promise<CustomerProfile | null> => {
  const cachedEntry = customerProfileCache.get(customerId);

  if (cachedEntry && 'data' in cachedEntry && cachedEntry.data) {
    return cachedEntry.data;
  }

  if (cachedEntry?.inFlightPromise) {
    return cachedEntry.inFlightPromise;
  }

  const inFlightPromise = loader()
    .then((profile) => {
      if (profile) {
        setCachedCustomerProfile(customerId, profile);
      } else {
        clearCachedCustomerProfile(customerId);
      }

      return profile;
    })
    .catch((error) => {
      clearCachedCustomerProfile(customerId);
      throw error;
    });

  customerProfileCache.set(customerId, {
    data: cachedEntry?.data ?? null,
    loadedAt: cachedEntry?.loadedAt,
    inFlightPromise,
  });

  return inFlightPromise;
};

export const normalizeEmailForComparison = (email: string | undefined): string =>
  (email || '').trim().toLowerCase();

export const isCachedProfileEmailStale = (
  cachedProfile: CustomerProfile | null | undefined,
  storeEmail: string | undefined,
): boolean => {
  const normalizedStoreEmail = normalizeEmailForComparison(storeEmail);
  const normalizedCachedEmail = normalizeEmailForComparison(cachedProfile?.email);

  if (!normalizedStoreEmail || !normalizedCachedEmail) {
    return false;
  }

  return normalizedStoreEmail !== normalizedCachedEmail;
};
